import { createTheme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    neutral: Palette['primary'];
  }
  interface PaletteOptions {
    neutral?: PaletteOptions['primary'];
  }
}

const theme = createTheme({
  palette: {
    primary: {
      main: '#0064FF', // Searce Blue (B5)
      light: '#3685FF', // B6
      dark: '#0051CB', // B4
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#734BFF', // Purple from tertiary palette
      light: '#9A7AFF',
      dark: '#5A3BCC',
      contrastText: '#FFFFFF',
    },
    neutral: {
      main: '#999999', // Light Grey
      light: '#E6E6E6', // Lightest Grey
      dark: '#232324', // Grey
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#F2F7FF', // B11 - lightest blue background
      paper: '#FFFFFF', // Pure White
    },
    text: {
      primary: '#000000', // Pure Black
      secondary: '#232324', // Grey
    },
    success: {
      main: '#66D89B', // Green from tertiary palette
      light: '#8FE5B5',
      dark: '#4DC882',
    },
    warning: {
      main: '#F4B643', // Orange from tertiary palette
      light: '#F7C870',
      dark: '#D99A2A',
    },
    error: {
      main: '#EA4255', // Red-Pink from tertiary palette
      light: '#F06B7A',
      dark: '#D32E42',
    },
    info: {
      main: '#00C8FF', // Turquoise from tertiary palette
      light: '#33D6FF',
      dark: '#00A0CC',
    },
  },
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 600,
      fontSize: '2.5rem',
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
      lineHeight: 1.3,
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      lineHeight: 1.3,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      lineHeight: 1.4,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.125rem',
      lineHeight: 1.4,
    },
    body1: {
      fontWeight: 400,
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontWeight: 400,
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    button: {
      fontWeight: 500,
      textTransform: 'none',
    },
    caption: {
      fontWeight: 400,
      fontSize: '0.75rem',
      lineHeight: 1.5,
    },
    overline: {
      fontWeight: 500,
      fontSize: '0.75rem',
      lineHeight: 1.5,
      textTransform: 'uppercase',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '8px 16px',
          fontWeight: 500,
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #E6E6E6', // Lightest Grey from official palette
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
          },
        },
      },
    },
  },
});

// Searce Brand Colors - Official Color Palette
// This is the single source of truth for all colors used across the application
// Based on the official Searce brand color palette
export const SEARCE_COLORS = {
  // Primary Colors - Searce Blue and variations
  primary: '#0064FF', // Searce Blue (B5) - Main brand color
  primaryDark: '#0051CB', // B4
  primary_600: '#0051CB', // B4
  primary_700: '#003583', // B3
  primary_800: '#002659', // B2
  primary_900: '#001630', // B1
  primary_light: '#3685FF', // B6
  primary_lighter: '#6DA6FF', // B8
  primary_lightest: '#79ADFF', // B7
  primary_dark: '#002659', // B2
  on_primary: '#FFFFFF', // Pure White

  // Secondary Colors - Purple from tertiary palette
  secondary: '#734BFF', // Purple from tertiary palette
  secondary_600: '#5A3BCC',
  secondary_700: '#4A2FA8',
  on_secondary: '#FFFFFF',

  // Success Colors - Green from tertiary palette
  success: '#66D89B', // Green from tertiary palette
  successDark: '#4DC882',
  success_400: '#8FE5B5',
  success_500: '#7ADFA8',
  success_600: '#4DC882',
  success_700: '#3AB86B',
  success_light: '#8FE5B5',
  on_success: '#FFFFFF',

  // Warning Colors - Orange from tertiary palette
  warning: '#F4B643', // Orange from tertiary palette
  warningDark: '#D99A2A',
  warning_600: '#D99A2A',
  warning_700: '#C0881F',
  warning_light: '#F7C870',
  on_warning: '#FFFFFF', // White for consistency

  // Error Colors - Red-Pink from tertiary palette
  error: '#EA4255', // Red-Pink from tertiary palette
  errorDark: '#D32E42',
  error_600: '#D32E42',
  error_700: '#B82538',
  danger: '#EA4255', // Alias for error
  danger_light: '#F06B7A',
  on_error: '#FFFFFF',

  // Info Colors - Turquoise from tertiary palette
  info: '#00C8FF', // Turquoise from tertiary palette
  info_600: '#00A0CC',
  info_700: '#0080A3',
  info_light: '#33D6FF',
  on_info: '#FFFFFF', // White for consistency

  // Neutral Colors - Official Grey palette
  neutral_100: '#F2F7FF', // B11 - lightest blue background
  neutral_200: '#E4EFFF', // B10
  neutral_300: '#C9DEFF', // B9
  neutral_400: '#E6E6E6', // Lightest Grey from primary palette
  neutral_500: '#999999', // Light Grey from primary palette
  neutral_600: '#232324', // Grey from primary palette
  neutral_700: '#1A1A1B', // Darker grey
  neutral_800: '#0F0F10', // Very dark grey
  neutral_900: '#000000', // Pure Black
  on_neutral_100: '#FFFFFF', // White text for consistency
  on_neutral_800: '#FFFFFF', // White text on dark background

  // Gray aliases for compatibility (mapped to official greys)
  gray_50: '#F2F7FF', // B11
  gray_100: '#E4EFFF', // B10
  gray_200: '#C9DEFF', // B9
  gray_300: '#E6E6E6', // Lightest Grey
  gray_400: '#999999', // Light Grey
  gray_500: '#999999', // Light Grey
  gray_600: '#232324', // Grey
  gray_700: '#1A1A1B', // Darker grey
  gray_800: '#0F0F10', // Very dark grey
  gray_900: '#000000', // Pure Black

  // Background Colors - Using official blue shades
  background: '#F2F7FF', // B11 - lightest blue background
  backgroundHover: '#E4EFFF', // B10
  cardBackground: '#FFFFFF', // Pure White
  sidebarBg: '#0064FF', // Searce Blue (B5)
  sidebarHover: '#0051CB', // B4
  border: '#E6E6E6', // Lightest Grey

  // Text Colors - Official palette
  textPrimary: '#000000', // Pure Black
  textSecondary: '#232324', // Grey
  white: '#FFFFFF', // Pure White
  black: '#000000', // Pure Black

  // Additional utility colors
  dark: '#000000', // Pure Black
  light: '#F2F7FF', // B11

  // Tertiary palette colors
  purple: '#734BFF', // Purple
  turquoise: '#00C8FF', // Turquoise
  green: '#66D89B', // Green
  orange: '#F4B643', // Orange
  redPink: '#EA4255', // Red-Pink
  blue: '#0064FF', // Searce Blue
};

export default theme; 